import React, { useState, useRef, useEffect } from 'react';
import { ISigningElement } from './PdfSigningSidebar';

interface ISigningElementProps {
  element: ISigningElement;
  isSelected: boolean;
  onSelect: () => void;
  onMove: (x: number, y: number) => void;
  onContentChange?: (content: string) => void;
  scale: number;
}

const SigningElement: React.FC<ISigningElementProps> = ({
  element,
  isSelected,
  onSelect,
  onMove,
  onContentChange,
  scale
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const elementRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    console.log('Mouse down on element, isEditing:', isEditing);
    if (isEditing) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Starting drag...');
    onSelect();
    setIsDragging(true);
    
    // Calculate offset from the mouse to the top-left corner of the element
    const rect = elementRef.current?.getBoundingClientRect();
    if (rect) {
      const offset = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
      console.log('Drag offset:', offset);
      setDragOffset(offset);
    }
  };

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    console.log('Mouse move, isDragging:', isDragging);
    if (!isDragging) return;
    
    console.log('Dragging to:', e.clientX, e.clientY);
    console.log('Drag offset:', dragOffset);
    
    const pdfPage = elementRef.current?.closest('.zpdf-page');
    
    if (pdfPage && elementRef.current) {
      const pageRect = pdfPage.getBoundingClientRect();
      const elementRect = elementRef.current.getBoundingClientRect();
      
      // Calculate new position relative to the PDF page
      const newX = (e.clientX - pageRect.left) - dragOffset.x;
      const newY = (e.clientY - pageRect.top) - dragOffset.y;
      
      // Constrain to page boundaries
      const maxX = pageRect.width - elementRect.width;
      const maxY = pageRect.height - elementRect.height;
      
      const constrainedX = Math.max(0, Math.min(newX, maxX));
      const constrainedY = Math.max(0, Math.min(newY, maxY));
      
      console.log('Moving to:', constrainedX, constrainedY);
      onMove(constrainedX, constrainedY);
    }
  }, [isDragging, dragOffset, onMove]);

  const handleMouseUp = () => {
    console.log('Mouse up - stopping drag');
    setIsDragging(false);
  };

  useEffect(() => {
    console.log('isDragging changed:', isDragging);
    if (isDragging) {
      console.log('Adding mouse event listeners');
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        console.log('Removing mouse event listeners');
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  const handleDoubleClick = () => {
    if (element.type === 'text' || element.type === 'signature') {
      setIsEditing(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onContentChange) {
      onContentChange(e.target.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
    }
  };

  const renderContent = () => {
    switch (element.type) {
      case 'signature':
        if (isEditing) {
          return (
            <input
              ref={inputRef}
              type="text"
              value={element.content || ''}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              className="signing-element-input signature-input"
              placeholder="Your signature"
              style={{ color: element.textColor || '#000000' }}
            />
          );
        }
        return (
          <span className="signature-text">
            {element.content || 'Signature'}
          </span>
        );
      
      case 'date':
        return (
          <span className="date-text">
            {new Date().toLocaleDateString()}
          </span>
        );
      
      case 'checkmark':
        return <span className="checkmark">✓</span>;
      
      case 'x':
        return <span className="x-mark">✗</span>;
      
      case 'text':
        if (isEditing) {
          return (
            <input
              ref={inputRef}
              type="text"
              value={element.content || ''}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              className="signing-element-input text-input"
              placeholder="Enter text"
              style={{ color: element.textColor || '#000000' }}
            />
          );
        }
        return (
          <span className="free-text">
            {element.content || 'Text'}
          </span>
        );
      
      default:
        return null;
    }
  };

  return (
    <div
      ref={elementRef}
      className={`signing-element signing-element-${element.type} ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
      style={{
        left: element.x,
        top: element.y,
        color: element.textColor || '#000000'
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {renderContent()}
    </div>
  );
};

export default SigningElement;